import { For, Show, createResource, createSignal } from "solid-js";

import AsyncResource from "../components/AsyncResource";
import CursorPager from "../components/CursorPager";
import { needsFirm } from "../components/FirmPicker";
import PageHeader from "../components/PageHeader";
import Pagination from "../components/Pagination";
import {
  api,
  type AuditActorKind,
  type AuditEventListCursor,
} from "../lib/api";
import { createCursorPages } from "../lib/cursorPages";
import { useSession } from "../lib/session";

const PAGE_SIZE = 50;

/** One audit row as both trails state it. */
interface AuditRow {
  event_id: string;
  created_at: string;
  actor_type: string;
  actor_label?: string | null;
  action: string;
  target_type: string;
  target_id?: string | null;
  message?: string | null;
}

/**
 * The firm's audit trail (Firm API) and, for platform staff, the platform's
 * own events: the rows that belong to no firm.
 */
export default function AuditLog() {
  const session = useSession();
  const superadmin = () => session.admin()?.role === "noctant_superadmin";
  const [trail, setTrail] = createSignal<"firm" | "platform">("firm");
  const [actorType, setActorType] = createSignal<AuditActorKind | "">("");
  const [action, setAction] = createSignal("");
  const [targetType, setTargetType] = createSignal("");
  const pages = createCursorPages<AuditEventListCursor>();
  const [offset, setOffset] = createSignal(0);
  const restart = () => {
    pages.reset();
    setOffset(0);
  };

  const filters = () => ({
    actor_type: actorType() || undefined,
    action: action() || undefined,
    target_type: targetType() || undefined,
  });
  const [firmPage] = createResource(
    () =>
      trail() === "firm" &&
      !needsFirm(session.admin()?.role ?? "") && {
        cursor: pages.current(),
        ...filters(),
      },
    ({ cursor, ...q }) =>
      api.listAuditEvents({ limit: PAGE_SIZE, ...q, ...cursor }),
  );
  const [platformPage] = createResource(
    () => trail() === "platform" && { offset: offset(), ...filters() },
    (q) => api.listPlatformAuditLog({ limit: PAGE_SIZE, ...q }),
  );

  return (
    <>
      <PageHeader
        title="Audit log"
        subtitle={
          trail() === "firm"
            ? "Every action in the firm"
            : "The platform's own events"
        }
      />

      <div class="card mb-4">
        <div class="card-body grid grid-cols-1 md:grid-cols-4 gap-3">
          <Show when={superadmin()}>
            <div>
              <label class="label" for="trail">
                Trail
              </label>
              <select
                id="trail"
                class="select"
                value={trail()}
                onInput={(e) => {
                  setTrail(e.currentTarget.value === "platform" ? "platform" : "firm");
                  restart();
                }}
              >
                <option value="firm">This firm</option>
                <option value="platform">Platform</option>
              </select>
            </div>
          </Show>
          <div>
            <label class="label" for="actor-type">
              Actor type
            </label>
            <select
              id="actor-type"
              class="select"
              value={actorType()}
              onInput={(e) => {
                const value = e.currentTarget.value;
                setActorType(
                  value === "operator" ||
                    value === "admin" ||
                    value === "trader" ||
                    value === "system"
                    ? value
                    : "",
                );
                restart();
              }}
            >
              <option value="">Any</option>
              <option value="operator">operator</option>
              <option value="admin">admin</option>
              <option value="trader">trader</option>
              <option value="system">system</option>
            </select>
          </div>
          <div>
            <label class="label" for="action">
              Action
            </label>
            <input
              id="action"
              class="input font-mono text-xs"
              placeholder="e.g. operator_api.challenge.reset"
              value={action()}
              onInput={(e) => {
                setAction(e.currentTarget.value.trim());
                restart();
              }}
            />
          </div>
          <div>
            <label class="label" for="target-type">
              Target type
            </label>
            <select
              id="target-type"
              class="select"
              value={targetType()}
              onInput={(e) => {
                setTargetType(e.currentTarget.value);
                restart();
              }}
            >
              <option value="">Any</option>
              <option value="user">user</option>
              <option value="account">account</option>
              <option value="challenge_instance">challenge_instance</option>
              <option value="challenge_product">challenge_product</option>
              <option value="operator">operator</option>
              <option value="organization">organization</option>
              <option value="restriction_layer">restriction_layer</option>
              <option value="kyc_record">kyc_record</option>
            </select>
          </div>
        </div>
      </div>

      <div class="card">
        <Show
          when={trail() === "firm"}
          fallback={
            <AsyncResource resource={platformPage}>
              {(data) => (
                <>
                  <AuditTable rows={data().items} />
                  <div class="card-body">
                    <Pagination
                      offset={offset()}
                      limit={PAGE_SIZE}
                      hasMore={data().has_more}
                      onOffset={setOffset}
                    />
                  </div>
                </>
              )}
            </AsyncResource>
          }
        >
          <AsyncResource resource={firmPage}>
            {(data) => (
              <>
                <AuditTable rows={data().events} />
                <div class="card-body">
                  <CursorPager pages={pages} next={data().next_cursor} />
                </div>
              </>
            )}
          </AsyncResource>
        </Show>
      </div>
    </>
  );
}

function AuditTable(props: { rows: AuditRow[] }) {
  return (
    <table class="data-table">
      <thead>
        <tr>
          <th>When</th>
          <th>Actor</th>
          <th>Action</th>
          <th>Target</th>
          <th>Message</th>
        </tr>
      </thead>
      <tbody>
        <For each={props.rows}>
          {(e) => (
            <tr>
              <td class="text-slate-500 whitespace-nowrap">
                {new Date(e.created_at).toLocaleString()}
              </td>
              <td>
                <div>{e.actor_type}</div>
                <div class="text-xs text-slate-500">{e.actor_label ?? "—"}</div>
              </td>
              <td class="font-mono text-xs">{e.action}</td>
              <td class="text-xs">
                <div>{e.target_type}</div>
                <div class="font-mono text-slate-500">{e.target_id ?? "—"}</div>
              </td>
              <td class="text-slate-700">{e.message ?? "—"}</td>
            </tr>
          )}
        </For>
        {props.rows.length === 0 && (
          <tr>
            <td colSpan={5} class="text-center text-slate-500 py-6">
              No events match these filters.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
